import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Loader2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_LINK = "https://wa.me/917996325666?text=Hi%20Shubham%2C%20I%20just%20visited%20ChillRefer%20and%20have%20a%20question";

// Auto-open the chat ONCE per user every 30 days (was: every page load after
// 15s, which was way too aggressive — users complained). Stored in
// localStorage as a UTC ISO timestamp; if absent OR older than 30 days, the
// chat is allowed to auto-open after `AUTO_OPEN_DELAY_MS` of dwell time.
const DISMISS_STORAGE_KEY = "chillrefer_chat_last_seen_at";
const AUTO_OPEN_DELAY_MS = 45_000; // 45s — gives the user time to read first
const REMIND_AFTER_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const SalesChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hey! Thinking about landing referrals at top companies? Ask me anything about ChillRefer." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-open ONCE every 30 days per user — and only after 45s of dwell time.
  // The previous behaviour (open after 15s every page load) was too aggressive
  // and users complained. We persist a UTC timestamp in localStorage and only
  // auto-open if it's missing OR more than 30 days old.
  useEffect(() => {
    if (hasInteracted) return;
    let lastSeen = null;
    try {
      const v = localStorage.getItem(DISMISS_STORAGE_KEY);
      if (v) lastSeen = new Date(v).getTime();
    } catch {
      // localStorage might be blocked — fall through to allow auto-open.
    }
    const now = Date.now();
    if (lastSeen && now - lastSeen < REMIND_AFTER_MS) {
      // Already nudged this user recently — don't auto-pop again.
      return;
    }
    const timer = setTimeout(() => {
      if (!open && !hasInteracted) {
        setOpen(true);
        // Record the timestamp the moment we auto-open so subsequent navigations
        // don't re-trigger the popup, even mid-session.
        try { localStorage.setItem(DISMISS_STORAGE_KEY, new Date().toISOString()); } catch {}
      }
    }, AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open, hasInteracted]);

  // Also record a "seen" timestamp when the user manually closes or interacts —
  // belt-and-suspenders so closing the popup once is honoured everywhere.
  const markSeen = () => {
    try { localStorage.setItem(DISMISS_STORAGE_KEY, new Date().toISOString()); } catch {}
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/sales-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMsg, session_id: sessionId }),
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: "assistant", text: data.response }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        text: `Having trouble connecting. Chat with Shubham, the founder, directly on WhatsApp!`
      }]);
    }
    setLoading(false);
  };

  const quickActions = [
    "How does ChillRefer work?",
    "Is my LinkedIn safe?",
    "What results can I expect?",
  ];

  return (
    <>
      {/* Chat Bubble */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={() => { setOpen(true); setHasInteracted(true); markSeen(); }}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#FACC15] text-black flex items-center justify-center shadow-[0_4px_24px_rgba(250,204,21,0.4)] hover:scale-110 transition-transform"
            data-testid="sales-chat-bubble"
          >
            <MessageSquare size={24} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Notification dot */}
      {!open && !hasInteracted && (
        <span className="fixed bottom-[72px] right-6 z-50 w-3 h-3 rounded-full bg-red-500 animate-pulse" />
      )}

      {/* Chat Window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[520px] rounded-2xl overflow-hidden flex flex-col shadow-[0_8px_40px_rgba(0,0,0,0.5)]"
            style={{ border: "1px solid rgba(255,255,255,0.1)", background: "#0c0c0e" }}
            data-testid="sales-chat-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3" style={{ background: "linear-gradient(135deg, #FACC15 0%, #eab308 100%)" }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-black/20 flex items-center justify-center">
                  <MessageSquare size={16} className="text-black" />
                </div>
                <div>
                  <p className="text-black font-bold text-sm">ChillRefer</p>
                  <p className="text-black/60 text-[10px]">Usually replies instantly</p>
                </div>
              </div>
              <button onClick={() => { setOpen(false); setHasInteracted(true); markSeen(); }} className="text-black/60 hover:text-black transition-colors">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ maxHeight: "340px", minHeight: "200px" }}>
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-[#FACC15]/15 text-white border border-[#FACC15]/20"
                        : "bg-white/5 text-white/80 border border-white/5"
                    }`}
                  >
                    {msg.text}
                    {/* Detect WhatsApp link in message */}
                    {msg.role === "assistant" && msg.text.includes("wa.me") && (
                      <a
                        href={WHATSAPP_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 flex items-center gap-1 text-xs text-[#25D366] font-bold hover:underline"
                      >
                        Chat on WhatsApp <ArrowRight size={12} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/5 border border-white/5 px-3 py-2 rounded-xl">
                    <Loader2 size={16} className="animate-spin text-[#FACC15]" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions (only show if no user messages yet) */}
            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                {quickActions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => { setInput(q); setHasInteracted(true); }}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/50 hover:text-[#FACC15] hover:border-[#FACC15]/30 transition-all"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* WhatsApp CTA */}
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="mx-4 mb-2 flex items-center justify-center gap-2 py-1.5 rounded-lg text-xs font-medium text-[#25D366] bg-[#25D366]/10 border border-[#25D366]/20 hover:bg-[#25D366]/20 transition-all"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Talk to the Founder
            </a>

            {/* Input */}
            <div className="px-3 pb-3 pt-1">
              <form
                onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2"
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask anything about ChillRefer..."
                  className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-white/30"
                  data-testid="sales-chat-input"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || loading}
                  className="text-[#FACC15] disabled:text-white/20 transition-colors"
                  data-testid="sales-chat-send"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SalesChatWidget;
